import { useState, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';
import DaumPostcodeSearch from '@/components/base/DaumPostcodeSearch';

type AddressValue = { zonecode: string; roadAddress: string; jibunAddress: string; detailAddress: string };

interface BasicInfoData {
  name: string;
  phone: string;
  gender: '남' | '여' | '';
  birthDate: string;
  address: AddressValue;
  avatarUrl: string;
}

interface BasicInfoStepProps {
  data: BasicInfoData;
  onChange: (data: Partial<BasicInfoData>) => void;
  onSubmit: () => void;
  onBack: () => void;
  role: 'patient' | 'caregiver';
  onChangeRole: () => void;
}

export default function BasicInfoStep({ data, onChange, onSubmit, onBack, role, onChangeRole }: BasicInfoStepProps) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);

    const fileExt = file.name.split('.').pop();
    const filePath = `avatars/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('profiles')
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      setUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage.from('profiles').getPublicUrl(filePath);
    onChange({ avatarUrl: urlData.publicUrl });
    setUploading(false);
  }

  const isComplete = data.name && data.phone && data.phone.length >= 10 && data.gender && data.birthDate && data.birthDate.length === 8 && data.address.roadAddress;

  return (
    <div className="space-y-5">
      {/* 프로필 사진 */}
      <div className="flex flex-col items-center gap-3">
        <div className="relative">
          <div
            className="w-24 h-24 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50 cursor-pointer"
            onClick={() => fileRef.current?.click()}
          >
            {data.avatarUrl ? (
              <img src={data.avatarUrl} alt="프로필" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center">
                <i className="ri-camera-line text-2xl text-gray-400" />
                <p className="text-[10px] text-gray-400 mt-1">사진 등록</p>
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-full">
                <i className="ri-loader-4-line animate-spin text-white text-xl" />
              </div>
            )}
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#2E7D6B] flex items-center justify-center cursor-pointer"
          >
            <i className="ri-camera-line text-white text-sm" />
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handlePhotoUpload}
        />
        <p className="text-xs text-gray-400">프로필 사진을 등록하면 매칭 확률이 높아집니다</p>
      </div>

      {/* 성명 */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          성명 <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={data.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="실명을 입력하세요"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white"
        />
      </div>

      {/* 휴대폰 */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          휴대폰 번호 <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          inputMode="numeric"
          value={data.phone}
          onChange={(e) => onChange({ phone: e.target.value.replace(/\D/g, '').slice(0, 11) })}
          placeholder="01012345678"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white"
        />
      </div>

      {/* 성별 */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          성별 <span className="text-red-400">*</span>
        </label>
        <div className="flex gap-3">
          {(['남', '여'] as const).map((g) => (
            <button
              key={g}
              onClick={() => onChange({ gender: g })}
              className={`flex-1 py-3 rounded-xl text-sm font-bold border-2 cursor-pointer transition-all ${
                data.gender === g
                  ? 'border-[#2E7D6B] text-[#2E7D6B] bg-[#f0f9f6]'
                  : 'border-gray-100 text-gray-500 bg-white hover:border-gray-200'
              }`}
            >
              {g}성
            </button>
          ))}
        </div>
      </div>

      {/* 생년월일 */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          생년월일 (8자리) <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          inputMode="numeric"
          value={data.birthDate}
          onChange={(e) => onChange({ birthDate: e.target.value.replace(/\D/g, '').slice(0, 8) })}
          placeholder="19900101"
          maxLength={8}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white"
        />
      </div>

      {/* 주소 */}
      <DaumPostcodeSearch
        value={data.address}
        onChange={(addr) => onChange({ address: addr })}
        label="주소"
        required
      />

      {/* 역할 확인 */}
      <div className="flex items-center justify-between py-2 px-4 rounded-xl bg-gray-50">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">선택한 역할:</span>
          <strong className="text-sm text-gray-800">
            {role === 'patient' ? '환자/보호자' : '간병인'}
          </strong>
        </div>
        <button onClick={onChangeRole} className="text-xs text-[#2E7D6B] font-medium cursor-pointer">
          변경
        </button>
      </div>

      {/* 액션 버튼 */}
      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-3.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 whitespace-nowrap cursor-pointer hover:bg-gray-200 transition-colors"
        >
          이전
        </button>
        <button
          onClick={onSubmit}
          disabled={!isComplete}
          className="flex-1 py-3.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50"
          style={{ backgroundColor: '#2E7D6B' }}
        >
          다음
        </button>
      </div>
    </div>
  );
}